import classnames from 'classnames';
import { FC, useCallback } from 'react';
import { Controller, useFormContext } from 'react-hook-form';
import { matchPath } from 'react-router-dom';

import dayjs from 'dayjs';
import { OperationsBlockTypes } from '../model/operations.types';

import { chakra, IconButton, Input, Table, Td, Text, Tr } from '@chakra-ui-kraud/react';
import { DatePicker, Select } from 'antd';

import { DEFAULT_YEAR_FORMAT, TECH_CARD_NEW, TechCardStatuses, UserRoles, useTechCard } from '@/shared';
import { useAppSelector } from '@/shared/state';
import { MadIcon } from 'madsoft-icons';

import styles from './custom-operations.module.scss';
import clsx from 'clsx';
import defaultStyles from '../../../tech-card.module.scss';
import { FormT } from '@/pages/tech-card-page/formConfig';
import { ProductOperationDomain } from '@/shared/state/api/swagger';
import { CustomOperationsHeader } from './custom-operations-header';
import { convertNumberToNumberStringWithDot } from '@/shared/core/utils/convert-string-to-number-string';

export const CustomOperationsBlock: FC<OperationsBlockTypes> = ({
	isEditable,
	employee,
	historedOperations,
	renderCancelButton,
	renderRenewOperationButton,
	setSideModalLinkOperation,
	focusedBlock,
	setFocusedBlock,
}) => {
	const { cardInfo } = useTechCard();

	const UserRole = useAppSelector((state) => state.auth.userProfile?.role);
	const { control, setValue, getValues } = useFormContext<FormT>();

	const { product_operations: fields, default_product_operations: defaultFields } = getValues();

	const isCreationMode = !!matchPath(TECH_CARD_NEW, location.pathname) && !cardInfo;

	const sortedOperationsTypes = fields ? [...fields].sort((a, b) => Number(a?.step_id) - Number(b?.step_id)) : [];

	const isStorekeeperRole = UserRole === UserRoles.storekeeper;

	const canRenew = UserRole !== UserRoles.storekeeper && cardInfo?.status === TechCardStatuses.progress;

	const isFieldEditable = useCallback(
		(index: number) => {
			if (!isEditable || !isStorekeeperRole) {
				return true;
			}
			// Кладовщик может редактировать только дефолтные операции
			const operation = fields?.[index];
			return defaultFields?.some((defOp) => defOp.id === operation?.id) ?? false;
		},
		[isEditable, isStorekeeperRole, fields, defaultFields],
	);

	const customIndex =
		Number(cardInfo?.product_operations?.length) + Number(cardInfo?.default_product_operations?.length);
	const customOperations = [{}, {}] as FormT['custom_operations'];

	// определение завершенной операции (если есть ФИО и дата) и пустого поля
	const isOperationDoneAndEmptyField = (
		operation: ProductOperationDomain,
		fieldValue: string | number | null | undefined,
	) => {
		if (!!operation.fio?.length && !!operation.date?.length && !fieldValue) {
			return true;
		}
		return false;
	};

	const emptyRows = (length: number) => {
		const rows = new Array(length).fill({});
		return rows.map((_, idx) => (
			<Tr key={idx}>
				<Td></Td>
				<Td></Td>
				<Td></Td>
				<Td></Td>
				<Td></Td>
				<Td></Td>
				<Td></Td>
				<Td></Td>
				<Td></Td>
				<Td></Td>
				<Td></Td>
			</Tr>
		));
	};

	// определение завершенной операции (если есть ФИО и дата)
	const isOperationDone = (operation: ProductOperationDomain) => {
		if (!!operation.fio?.length && !!operation.date?.length) {
			return true;
		}
		return false;
	};

	const handleCalculateWasted = useCallback(
		(row: number) => {
			const countOut = Number(getValues(`product_operations.${row}.count_out_number`));
			const countIn = Number(getValues(`product_operations.${row}.count_in_number`));

			if (!countOut || Number.isNaN(countOut) || !countIn || Number.isNaN(countIn)) {
				setValue(
					// остаток от (всего штук, грамм - годны штук, грамм) переходит в брак (штук) этой операции
					`product_operations.${row}.wasted`,
					undefined,
				);
			} else {
				setValue(
					// остаток от (всего штук, грамм - годны штук, грамм) переходит в брак (штук) этой операции
					`product_operations.${row}.wasted`,
					countIn - countOut,
				);
			}
		},
		[getValues, setValue],
	);

	return (
		<Table
			className={clsx(
				focusedBlock?.block === 'operations' &&
					focusedBlock.focusedBy === 'tab' &&
					!focusedBlock.atCurrentBlock &&
					defaultStyles.focused,
				isEditable && styles['edit-table'],
			)}
			onFocus={() => setFocusedBlock && setFocusedBlock('operations')}
		>
			<CustomOperationsHeader />
			{!isEditable && !isCreationMode && (
				<>
					{cardInfo?.product_operations?.map((operation) => (
						<Tr key={operation.id} className={operation.is_canceled ? styles['field-disabled'] : ''}>
							<Td style={{ textAlign: 'left' }}>
								{operation.step_id < 10 ? '0' + operation.step_id : operation.step_id}
							</Td>
							<Td style={{ textAlign: 'left', padding: '0px 2px 0px 8px' }}>
								{!operation.has_link ? (
									operation.canceled_by ? (
										<Text>{`${operation.name} — Выполнена в МК ${operation.canceled_by?.canceled_by_preform_tech_map?.number}`}</Text>
									) : (
										<Text>{`${operation.name} ${
											operation.is_canceled ? '— операция отменена' : ''
										}`}</Text>
									)
								) : (
									<chakra.div display="flex" justifyContent="space-between" alignItems="center" p={0}>
										<chakra.div>
											{operation.name}
											{operation.id && <sup>{historedOperations[operation.id]}</sup>}
											{operation.is_canceled ? '— операция отменена' : ''}
										</chakra.div>
										<IconButton
											aria-label=""
											size="sm"
											variant="ghost"
											colorScheme="primary"
											onClick={() =>
												setSideModalLinkOperation({
													isOpen: true,
													title: `${operation.step_id} - ${operation.name}`,
													operationId: Number(operation.id),
												})
											}
											icon={
												<MadIcon
													module="basic"
													size="default"
													type="outline"
													mode="default"
													name="link"
												/>
											}
										/>
									</chakra.div>
								)}
							</Td>
							<Td>
								{dayjs(operation.date).isValid()
									? dayjs(operation.date).format(DEFAULT_YEAR_FORMAT)
									: ''}
							</Td>
							<Td>
								{!isOperationDoneAndEmptyField(operation, operation.count_in_gram)
									? operation.count_in_gram
									: '-'}
							</Td>
							<Td>
								{!isOperationDoneAndEmptyField(operation, operation.count_in_number)
									? operation.count_in_number
									: '-'}
							</Td>
							<Td>{operation.fio}</Td>
							<Td>
								{!isOperationDoneAndEmptyField(operation, operation.count_out_gram)
									? operation.count_out_gram
									: '-'}
							</Td>
							<Td>
								{!isOperationDoneAndEmptyField(operation, operation.count_out_number)
									? operation.count_out_number
									: '-'}
							</Td>
							<Td>{!operation.wasted ? '' : operation.wasted}</Td>
							<Td></Td>
							<Td></Td>
						</Tr>
					))}
					{emptyRows(2)}
				</>
			)}
			{!isEditable && isCreationMode && sortedOperationsTypes?.length ? (
				<>
					{sortedOperationsTypes?.map((operation) => (
						<Tr key={operation.id} className={operation.is_canceled ? styles['field-disabled'] : ''}>
							<Td style={{ textAlign: 'left' }}>
								{operation.step_id < 10 ? '0' + operation.step_id : operation.step_id}
							</Td>
							<Td style={{ textAlign: 'left' }}>{operation.name}</Td>
							<Td></Td>
							<Td></Td>
							<Td></Td>
							<Td></Td>
							<Td></Td>
							<Td></Td>
							<Td></Td>
							<Td></Td>
							<Td></Td>
						</Tr>
					))}

					{emptyRows(2)}
				</>
			) : (
				<></>
			)}
			{isEditable &&
				!isCreationMode &&
				fields?.map((field, index) => {
					const fieldEditable = !field.is_canceled && isFieldEditable(index);
					return (
						<Tr key={field.id}>
							<Td
								className={field.is_canceled ? styles['field-disabled'] : ''}
								style={{ textAlign: 'left' }}
							>
								{fieldEditable ? (
									<Controller
										name={`product_operations.${index}.step_id`}
										control={control}
										render={({ field: { onChange, value }, fieldState: { error } }) => (
											<Input
												backgroundColor="transparent"
												tabIndex={7}
												isInvalid={!!error}
												onChange={onChange}
												value={value}
												name={`product_operations.${index}.step_id`}
											/>
										)}
									/>
								) : (
									<Text p="6px">{field.step_id}</Text>
								)}
							</Td>
							<Td className={field.is_canceled ? styles['field-disabled'] : ''}>
								<chakra.div
									w="100%"
									h="100%"
									display="flex"
									justifyContent="space-between"
									alignItems="center"
								>
									{fieldEditable ? (
										<Controller
											name={`product_operations.${index}.name`}
											control={control}
											render={({ field: fieldData, fieldState: { error } }) => {
												return (
													<Input
														tabIndex={7}
														isInvalid={!!error}
														{...fieldData}
														h="100%"
														flex="1"
													/>
												);
											}}
										/>
									) : field.canceled_by ? (
										<Text
											textAlign="left"
											padding={'8px 1px 8px 6px'}
											fontWeight="normal"
										>{`${field.name} — Выполнена в МК ${field.canceled_by.canceled_by_preform_tech_map?.number}`}</Text>
									) : (
										<Text textAlign="left" padding={'8px 1px 8px 6px'} fontWeight="normal">
											{field.name}
										</Text>
									)}
									<Controller
										name={`product_operations.${index}.is_canceled`}
										control={control}
										render={() => {
											if (field.is_canceled) {
												return canRenew ? (
													<>{renderRenewOperationButton(field, 'operation')}</>
												) : (
													<></>
												);
											}
											// если в операции нет даты или нет ФИО исполнителя
											if (fieldEditable && (!field.date || !field.fio)) {
												return <>{renderCancelButton(field, 'operation')}</>;
											}
											return <></>;
										}}
									/>
								</chakra.div>
							</Td>
							<Td className={clsx(styles['date-style'], field.is_canceled && styles['field-disabled'])}>
								{fieldEditable ? (
									<Controller
										name={`product_operations.${index}.date`}
										control={control}
										render={({ field: { onChange }, fieldState: { error } }) => (
											<DatePicker
												style={{ width: '100%', height: '100%', padding: '0' }}
												tabIndex={7}
												status={error ? 'error' : ''}
												onChange={onChange}
												format={DEFAULT_YEAR_FORMAT}
												defaultValue={
													field.date && dayjs(field.date).isValid()
														? dayjs(field.date)
														: undefined
												}
												name={`product_operations.${index}.date`}
											/>
										)}
									/>
								) : (
									<Text fontWeight="normal" textAlign="left">
										{field.date ? dayjs(field.date).format(DEFAULT_YEAR_FORMAT) : ''}
									</Text>
								)}
							</Td>
							<Td className={field.is_canceled ? styles['field-disabled'] : ''}>
								{fieldEditable && (!isOperationDone(field) || UserRole === UserRoles.admin) ? (
									<Controller
										name={`product_operations.${index}.count_in_gram`}
										control={control}
										render={({ field: { onChange, value }, fieldState: { error } }) => (
											<Input
												backgroundColor="transparent"
												tabIndex={7}
												onChange={onChange}
												isInvalid={!!error}
												value={value}
												name={`product_operations.${index}.count_in_gram`}
											/>
										)}
									/>
								) : (
									<Text
										p="6px"
										fontWeight="normal"
										textAlign={
											!isOperationDoneAndEmptyField(field, field.count_in_gram)
												? 'left'
												: 'center'
										}
									>
										{!isOperationDoneAndEmptyField(field, field.count_in_gram)
											? field.count_in_gram
											: '-'}
									</Text>
								)}
							</Td>
							<Td className={field.is_canceled ? styles['field-disabled'] : ''}>
								{fieldEditable && (!isOperationDone(field) || UserRole === UserRoles.admin) ? (
									<Controller
										name={`product_operations.${index}.count_in_number`}
										control={control}
										render={({ field: { onChange, value }, fieldState: { error } }) => (
											<Input
												backgroundColor="transparent"
												tabIndex={7}
												onChange={(e) => {
													onChange(e);
													handleCalculateWasted(index);
												}}
												isInvalid={!!error}
												value={value}
												name={`product_operations.${index}.count_in_number`}
											/>
										)}
									/>
								) : (
									<Text
										p="6px"
										fontWeight="normal"
										textAlign={
											!isOperationDoneAndEmptyField(field, field.count_in_number)
												? 'left'
												: 'center'
										}
									>
										{!isOperationDoneAndEmptyField(field, field.count_in_number)
											? field.count_in_number
											: '-'}
									</Text>
								)}
							</Td>
							<Td className={field.is_canceled ? styles['field-disabled'] : ''}>
								{fieldEditable ? (
									<Controller
										name={`product_operations.${index}.fio`}
										control={control}
										render={({ field: { value, onChange } }) => (
											<Select
												tabIndex={7}
												filterOption={(input, option) =>
													(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
												}
												allowClear
												showSearch
												placeholder="Выберите ФИО"
												optionFilterProp="children"
												value={value}
												onChange={(e) => {
													onChange(e);
													setValue(
														`product_operations.${index}.fio`,
														e ? employee.find((el) => el.value === e)?.label : '',
													);
												}}
												options={employee}
												style={{ width: '100%' }}
												className={classnames(styles['custom-select'])}
											/>
										)}
									/>
								) : (
									<Text p="6px" fontWeight="normal" textAlign="center">
										{field.fio}
									</Text>
								)}
							</Td>
							<Td className={field.is_canceled ? styles['field-disabled'] : ''}>
								{fieldEditable &&
								!field.is_canceled &&
								(!isOperationDone(field) || UserRole === UserRoles.admin) ? (
									<Controller
										name={`product_operations.${index}.count_out_gram`}
										control={control}
										render={({
											field: { onChange, value },
											fieldState: { error },
											formState: { defaultValues },
										}) => (
											<Input
												backgroundColor="transparent"
												tabIndex={7}
												isInvalid={!!error}
												onChange={(e) => {
													onChange(e);
													// Нужен каст потому что react-hook-form не поддерживает undefined значения для значений инпутов (значение поля не меняется)
													// https://github.com/orgs/react-hook-form/discussions/5858
													let parsedValue: number | undefined = e.target.value
														? Number(convertNumberToNumberStringWithDot(e.target.value))
														: ('' as unknown as undefined);
													parsedValue = Number.isNaN(parsedValue)
														? ('' as unknown as undefined)
														: parsedValue;
													const activeOperations = fields.filter((el) => !el.is_canceled);
													const opIdx = activeOperations.findIndex(
														(el) => el.id === field.id,
													);

													// если это последняя операция перед дефолтными (у дефолтных нельзя менять имена)
													// или если после операции есть только отмененные операции, но есть еще и дефолтные (не отмененные)

													if (opIdx === activeOperations.length - 2) {
														// перенос годных из "Контрольная ПСИ" в  раздел Кол-во "Сдача на СГД"
														// гр., предпоследняя кастомная операция -> первая дефолтная операция
														setValue(
															'default_product_operations.0.count_in_gram',
															parsedValue,
														);
													} else if (opIdx === activeOperations.length - 1) {
														// перенос годных из  "Консервации" в раздел Кол-во "Приёмка на СГД"
														// гр.,последная кастомная операция  -> вторая дефолтная операция
														setValue(
															'default_product_operations.1.count_in_gram',
															parsedValue,
														);
													} else {
														// поиск следующей неотмененной операции
														const currentOperationIndex = fields.findIndex(
															(el) => el.step_id > field.step_id && !el.is_canceled,
														);

														if (
															currentOperationIndex > -1 &&
															!defaultValues?.product_operations?.[currentOperationIndex]
																?.count_in_gram
														) {
															// если это не последняя операция, расчет годных (грамм) в этой операции переходит в грамм, штук следующей НЕ ОТМЕНЕННОЙ операции
															setValue(
																`product_operations.${currentOperationIndex}.count_in_gram`,
																parsedValue,
															);
														}
													}
												}}
												value={value}
												name={`product_operations.${index}.count_out_gram`}
											/>
										)}
									/>
								) : (
									<Text
										p="6px"
										fontWeight="normal"
										textAlign={
											!isOperationDoneAndEmptyField(field, field.count_out_gram)
												? 'left'
												: 'center'
										}
									>
										{!isOperationDoneAndEmptyField(field, field.count_out_gram)
											? field.count_out_gram
											: '-'}
									</Text>
								)}
							</Td>
							<Td className={field.is_canceled ? styles['field-disabled'] : ''}>
								{fieldEditable &&
								!field.is_canceled &&
								(!isOperationDone(field) || UserRole === UserRoles.admin) ? (
									<Controller
										name={`product_operations.${index}.count_out_number`}
										control={control}
										render={({
											field: { onChange, value },
											fieldState: { error },
											formState: { defaultValues },
										}) => (
											<Input
												backgroundColor="transparent"
												tabIndex={7}
												isInvalid={!!error}
												onChange={(e) => {
													onChange(e);
													// Нужен каст потому что react-hook-form не поддерживает undefined значения для значений инпутов (значение поля не меняется)
													// https://github.com/orgs/react-hook-form/discussions/5858
													let parsedValue: number | undefined = e.target.value
														? Number(convertNumberToNumberStringWithDot(e.target.value))
														: ('' as unknown as undefined);
													parsedValue = Number.isNaN(parsedValue)
														? ('' as unknown as undefined)
														: parsedValue;
													const activeOperations = fields.filter((el) => !el.is_canceled);
													const opIdx = activeOperations.findIndex(
														(el) => el.id === field.id,
													);

													// если это последняя операция перед дефолтными (у дефолтных нельзя менять имена)
													// или если после операции есть только отмененные операции, но есть еще и дефолтные (не отмененные)
													if (opIdx === activeOperations.length - 2) {
														// перенос годных из "Контрольная ПСИ" в  раздел Кол-во "Сдача на СГД"
														// шт., предпоследняя кастомная операция -> первая дефолтная операция
														setValue(
															'default_product_operations.0.count_in_number',
															parsedValue,
															{
																shouldValidate: true,
															},
														);
														// Пересчитываем брак в дефолтной операции
														const countOut = Number(
															getValues('default_product_operations.0.count_out_number'),
														);
														if (parsedValue && countOut) {
															setValue(
																'default_product_operations.0.wasted',
																parsedValue - countOut,
															);
														} else if (!countOut) {
															setValue('default_product_operations.0.wasted', undefined);
														}
													} else if (opIdx === activeOperations.length - 1) {
														// перенос годных из  "Консервации" в раздел Кол-во "Приёмка на СГД"
														// шт.,последная кастомная операция  -> вторая дефолтная операция
														setValue(
															'default_product_operations.1.count_in_number',
															parsedValue,
															{
																shouldValidate: true,
															},
														);
														// Пересчитываем брак в дефолтной операции
														const countOut = Number(
															getValues('default_product_operations.1.count_out_number'),
														);
														if (parsedValue && countOut) {
															setValue(
																'default_product_operations.1.wasted',
																parsedValue - countOut,
															);
														} else if (!countOut) {
															setValue('default_product_operations.1.wasted', undefined);
														}
													} else {
														// поиск следующей неотмененной операции
														const currentOperationIndex = fields.findIndex(
															(el) => el.step_id > field.step_id && !el.is_canceled,
														);

														if (
															currentOperationIndex > -1 &&
															!defaultValues?.product_operations?.[currentOperationIndex]
																?.count_in_number
														) {
															// если это не последняя операция, расчет годных (штук) в этой операции переходит в грамм, штук следующей НЕ ОТМЕНЕННОЙ операции
															setValue(
																`product_operations.${currentOperationIndex}.count_in_number`,
																parsedValue,
															);
														}
													}

													handleCalculateWasted(index);
												}}
												value={value}
												name={`product_operations.${index}.count_out_number`}
											/>
										)}
									/>
								) : (
									<Text
										p="6px"
										fontWeight="normal"
										textAlign={
											!isOperationDoneAndEmptyField(field, field.count_out_number)
												? 'left'
												: 'center'
										}
									>
										{!isOperationDoneAndEmptyField(field, field.count_out_number)
											? field.count_out_number
											: '-'}
									</Text>
								)}
							</Td>
							<Td className={field.is_canceled ? styles['field-disabled'] : ''}>
								{fieldEditable ? (
									<Controller
										name={`product_operations.${index}.wasted`}
										control={control}
										render={({ field: { onChange, value, name }, fieldState: { error } }) => (
											<Input
												isReadOnly={
													UserRole === UserRoles.operator ||
													UserRole === UserRoles.senior_operator
												}
												tabIndex={7}
												isInvalid={!!error}
												onChange={onChange}
												value={value && value > 0 ? value : ''}
												name={name}
												className={field.is_canceled ? styles['field-disabled'] : ''}
											/>
										)}
									/>
								) : (
									<Text p="6px" fontWeight="normal" textAlign="left">
										{!field.wasted ? '' : field.wasted}
									</Text>
								)}
							</Td>
							<Td className={field.is_canceled ? styles['field-disabled'] : ''}></Td>
							<Td className={field.is_canceled ? styles['field-disabled'] : ''}></Td>
						</Tr>
					);
				})}

			{/* Пустые строки в таблице */}
			{isEditable &&
				!isCreationMode &&
				(isStorekeeperRole
					? emptyRows(2)
					: customOperations?.map((field, index) => (
							<Tr key={customIndex + index}>
								<Td>
									<Controller
										name={`custom_operations.${customIndex + index}.step_id`}
										control={control}
										render={({ field: { onChange, value }, fieldState: { error } }) => (
											<Input
												tabIndex={7}
												isInvalid={!!error}
												onChange={onChange}
												value={value}
												name={`custom_operations.${customIndex + index}.step_id`}
											/>
										)}
									/>
								</Td>
								<Td>
									<Controller
										name={`custom_operations.${customIndex + index}.name`}
										control={control}
										render={({ field: { onChange, value }, fieldState: { error } }) => (
											<Input
												tabIndex={7}
												isInvalid={!!error}
												onChange={onChange}
												value={value}
												name={`custom_operations.${customIndex + index}.name`}
											/>
										)}
									/>
								</Td>
								<Td>
									<Controller
										name={`custom_operations.${customIndex + index}.date`}
										control={control}
										render={({ field: { onChange }, fieldState: { error } }) => (
											<DatePicker
												style={{ width: '100%' }}
												tabIndex={7}
												status={error ? 'error' : ''}
												onChange={onChange}
												defaultValue={
													field.date && dayjs(field.date).isValid()
														? dayjs(field.date)
														: undefined
												}
												format={DEFAULT_YEAR_FORMAT}
												name={`custom_operations.${customIndex + index}.date`}
											/>
										)}
									/>
								</Td>
								<Td>
									<Controller
										name={`custom_operations.${customIndex + index}.count_in_gram`}
										control={control}
										render={({ field: { onChange, value }, fieldState: { error } }) => (
											<Input
												tabIndex={7}
												onChange={onChange}
												isInvalid={!!error}
												value={value}
												name={`custom_operations.${customIndex + index}.count_in_gram`}
											/>
										)}
									/>
								</Td>
								<Td>
									<Controller
										name={`custom_operations.${customIndex + index}.count_in_number`}
										control={control}
										render={({ field: { onChange, value }, fieldState: { error } }) => (
											<Input
												tabIndex={7}
												onChange={onChange}
												isInvalid={!!error}
												value={value}
												name={`custom_operations.${customIndex + index}.count_in_number`}
											/>
										)}
									/>
								</Td>
								<Td>
									<Controller
										name={`custom_operations.${customIndex + index}.fio`}
										control={control}
										render={({ field: { value, onChange } }) => (
											<Select
												allowClear
												tabIndex={7}
												filterOption={(input, option) =>
													(option?.label ?? '').toLowerCase().includes(input.toLowerCase())
												}
												showSearch
												placeholder="Выберите ФИО"
												optionFilterProp="children"
												value={value}
												onChange={(e) => {
													onChange(e);
													setValue(
														`custom_operations.${customIndex + index}.fio`,
														e ? employee.find((el) => el.value === e)?.label : '',
													);
												}}
												options={employee}
												style={{ width: '100%' }}
												className={styles['custom-select']}
											/>
										)}
									/>
								</Td>
								<Td>
									<Controller
										name={`custom_operations.${customIndex + index}.count_out_gram`}
										control={control}
										render={({ field: { onChange, value }, fieldState: { error } }) => (
											<Input
												tabIndex={7}
												isInvalid={!!error}
												onChange={onChange}
												value={value}
												name={`custom_operations.${customIndex + index}.count_out_gram`}
											/>
										)}
									/>
								</Td>
								<Td>
									<Controller
										name={`custom_operations.${customIndex + index}.count_out_number`}
										control={control}
										render={({ field: { onChange, value }, fieldState: { error } }) => (
											<Input
												tabIndex={7}
												isInvalid={!!error}
												onChange={onChange}
												value={value}
												name={`custom_operations.${customIndex + index}.count_out_number`}
											/>
										)}
									/>
								</Td>
								<Td>
									<Controller
										name={`custom_operations.${customIndex + index}.wasted`}
										control={control}
										render={({ field: { onChange, value, name }, fieldState: { error } }) => (
											<Input
												isReadOnly={
													UserRole === UserRoles.operator ||
													UserRole === UserRoles.senior_operator
												}
												tabIndex={7}
												isInvalid={!!error}
												onChange={onChange}
												value={value && value > 0 ? value : ''}
												name={name}
											/>
										)}
									/>
								</Td>
								<Td></Td>
								<Td></Td>
							</Tr>
					  )))}
		</Table>
	);
};
